import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, AlertTriangle, FileBadge, Calendar, Building2, Cpu, Ruler, Weight } from 'lucide-react';
import { apiClient, unwrap } from '@/api/client';
import { formatNumber } from '@/utils/currency';

/**
 * Page publique de traçabilité QR (Sprint LB-C).
 * Accessible sans authentification — destinée à être affichée après scan
 * d'un QR code sur un lot livré sur chantier.
 * URL : /qr/lot/:id (référencée dans le qrCode stocké en DB par LotsProductionService).
 */

interface QrLotData {
  id:                string;
  lotNumber:         string;
  quantite:          string;
  dateProduction:    string;
  statut:            'en_production' | 'valide' | 'rejete';
  controleQualiteOk: boolean | null;
  fc28Mesure:        string | null;
  qrCode:            string;
  filiale: {
    filialeKey: string;
    name:       string;
    fullName:   string;
    pays:       string;
    logoPath:   string;
    metier:     string;
  };
  ordre: {
    reference: string;
    produit: {
      code:           string;
      designation:    string;
      famille:        string;
      sousFamille:    string | null;
      norme:          string | null;
      classeProduit:  string | null;
      resistanceFc28: string | null;
      uniteVente:     string;
    };
  };
}

const STATUT_LABEL: Record<QrLotData['statut'], { label: string; color: string; icon: typeof ShieldCheck }> = {
  en_production: { label: 'En production',    color: '#1E70B2', icon: Cpu },
  valide:        { label: 'Validé',           color: '#1F845C', icon: ShieldCheck },
  rejete:        { label: 'Non conforme',     color: '#dc2626', icon: AlertTriangle },
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', {
  weekday: 'long', day: '2-digit', month: 'long', year: 'numeric',
});

export const QrLotPage = () => {
  const { id } = useParams<{ id: string }>();
  const [lot, setLot]     = useState<QrLotData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    apiClient.get(`/qr/lot/${id}`)
      .then((res) => setLot(unwrap<QrLotData>(res)))
      .catch((err: unknown) => {
        const msg = (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
        setError(msg ?? 'Lot introuvable ou supprimé');
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div style={pageWrap}><div style={card}>Chargement…</div></div>
  );

  if (error || !lot) return (
    <div style={pageWrap}>
      <div style={card}>
        <AlertTriangle size={40} style={{ color: '#dc2626', margin: '0 auto 12px', display: 'block' }} />
        <h1 style={{ fontSize: 18, fontWeight: 700, textAlign: 'center', marginBottom: 8 }}>Lot introuvable</h1>
        <p style={{ fontSize: 13, color: '#666', textAlign: 'center' }}>{error}</p>
      </div>
    </div>
  );

  const statutInfo = STATUT_LABEL[lot.statut];
  const StatutIcon = statutInfo.icon;
  const conforme   = lot.controleQualiteOk;
  const isPoteauNF = lot.ordre.produit.famille === 'POTEAUX_BA' && lot.ordre.produit.norme === 'NF C 67-200';

  return (
    <div style={pageWrap}>
      <div style={card}>
        {/* Logo + nom filiale */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 16, borderBottom: '1px solid #e5e7eb' }}>
          <img src={lot.filiale.logoPath} alt={lot.filiale.name} style={{ height: 40, width: 'auto' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <div>
            <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 0.5 }}>Traçabilité produit</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{lot.filiale.fullName}</div>
            <div style={{ fontSize: 11, color: '#888' }}>{lot.filiale.pays} · {lot.filiale.metier}</div>
          </div>
        </div>

        {/* QR code central */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0', background: 'linear-gradient(180deg, #f9fafb 0%, white 100%)' }}>
          <div style={{ padding: 14, background: 'white', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
            <QRCodeSVG value={lot.qrCode} size={180} level="M" />
          </div>
        </div>

        {/* Identifiant lot */}
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: 1 }}>Numéro de lot</div>
          <div style={{ fontSize: 28, fontWeight: 800, fontFamily: 'monospace', color: '#111' }}>{lot.lotNumber}</div>
        </div>

        {/* Badge statut */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 999,
          background: statutInfo.color + '15', color: statutInfo.color,
          fontWeight: 700, fontSize: 14, margin: '0 auto 20px', width: 'fit-content',
        }}>
          <StatutIcon size={18} />
          {statutInfo.label}
          {isPoteauNF && conforme !== null && (
            <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 4 }}>
              · NF C 67-200 {conforme ? '✅' : '❌'}
            </span>
          )}
        </div>

        {/* Détails produit */}
        <div style={infoGrid}>
          <InfoRow icon={FileBadge} label="Produit" value={`${lot.ordre.produit.code} — ${lot.ordre.produit.designation}`} />
          <InfoRow icon={Building2} label="Famille" value={`${lot.ordre.produit.famille}${lot.ordre.produit.sousFamille ? ` · ${lot.ordre.produit.sousFamille}` : ''}`} />
          {lot.ordre.produit.norme && (
            <InfoRow icon={ShieldCheck} label="Norme" value={`${lot.ordre.produit.norme}${lot.ordre.produit.classeProduit ? ` — classe ${lot.ordre.produit.classeProduit}` : ''}`} />
          )}
          {lot.ordre.produit.resistanceFc28 && (
            <InfoRow icon={Weight} label="Résistance théorique" value={`fc28 ≥ ${lot.ordre.produit.resistanceFc28} MPa`} />
          )}
          {lot.fc28Mesure && (
            <InfoRow icon={Weight} label="Résistance mesurée" value={`${lot.fc28Mesure} MPa`} highlight={isPoteauNF} />
          )}
          <InfoRow icon={Ruler} label="Quantité" value={`${formatNumber(parseFloat(lot.quantite))} ${lot.ordre.produit.uniteVente}`} />
          <InfoRow icon={Calendar} label="Date production" value={fmtDate(lot.dateProduction)} />
          <InfoRow icon={FileBadge} label="OF d'origine" value={lot.ordre.reference} />
        </div>

        {/* Footer */}
        <div style={{ paddingTop: 16, marginTop: 20, borderTop: '1px solid #e5e7eb', textAlign: 'center', fontSize: 10, color: '#9ca3af' }}>
          SIM GROUP Holding Platform · ID lot {lot.id.slice(0, 8)}…
        </div>
      </div>
    </div>
  );
};

const InfoRow = ({ icon: Icon, label, value, highlight }: { icon: typeof ShieldCheck; label: string; value: string; highlight?: boolean }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
    <Icon size={16} style={{ color: '#666', flexShrink: 0, marginTop: 2 }} />
    <div style={{ flex: 1 }}>
      <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: highlight ? 700 : 500, color: highlight ? '#1F845C' : '#111' }}>{value}</div>
    </div>
  </div>
);

const pageWrap: React.CSSProperties = {
  minHeight:    '100vh',
  background:   'linear-gradient(135deg, #f5f5f4 0%, #e7e5e4 100%)',
  display:      'flex',
  alignItems:   'center',
  justifyContent: 'center',
  padding:      16,
  fontFamily:   'system-ui, -apple-system, sans-serif',
};

const card: React.CSSProperties = {
  background:   'white',
  borderRadius: 16,
  boxShadow:    '0 4px 24px rgba(0,0,0,0.08)',
  padding:      24,
  maxWidth:     400,
  width:        '100%',
};

const infoGrid: React.CSSProperties = {
  background:   '#fafafa',
  borderRadius: 8,
  padding:      '0 14px',
};
